import { faHistory } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    DatePickerAtom,
    TextFieldAtom,
} from "../../../../components/atoms/atom";
import { computeCashDiscountDeadline } from "../../../../utils/invoiceV2Utils";
import {
    selectFormErrors,
    selectFormState,
    setFormField,
    setValidationError,
} from "../../../../store/slices/invoiceV2Slice";
import { selectCurrentProjectId } from "../../../../store/slices/projectDetailsSlice";
import { useGetProjectTicketCategoriesQuery } from "../../../../store/services/ticketCategoriesService";

function InvoiceDetailFieldsV2() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const formValues = useSelector(selectFormState);
    const errors = useSelector(selectFormErrors);
    const projectId = useSelector(selectCurrentProjectId);

    const { data: categoriesResponse } = useGetProjectTicketCategoriesQuery(
        { projectId },
        { skip: !projectId },
    );
    // Use the full categories list - it carries cashDiscount / cashDiscountDays.
    // (The with-lv-status endpoint only returns id/name/flags.)
    const ticketCategories = categoriesResponse?.items ?? [];

    const readonly = formValues.readonly || false;

    const selectedCategory = ticketCategories.find(
        (cat) => cat.id === formValues.selectedTicketCategory?.value,
    );
    const cashDiscount = Number(selectedCategory?.cashDiscount ?? 0);
    const hasCashDiscount = cashDiscount > 0;
    const cashDiscountDays = selectedCategory?.cashDiscountDays ?? null;

    const handleChange = (field, value) => {
        if (field === "billingPeriodFrom" || field === "billingPeriodTo") {
            if (formValues.invoiceDate) {
                if (field === "billingPeriodFrom" && value > formValues.invoiceDate) {
                    dispatch(setValidationError({ field: "billingPeriodFrom", error: "error.billing-period-from-after-invoice-date" }));
                    return;
                }
                if (field === "billingPeriodTo" && value > formValues.invoiceDate) {
                    dispatch(setValidationError({ field: "billingPeriodTo", error: "error.billing-period-to-after-invoice-date" }));
                    return;
                }
            }
            if (field === "billingPeriodTo" && formValues.billingPeriodFrom && value < formValues.billingPeriodFrom) {
                dispatch(setValidationError({ field: "billingPeriodTo", error: "error.billing-period-to-before-from" }));
                return;
            }
            if (field === "billingPeriodFrom" && formValues.billingPeriodTo && value > formValues.billingPeriodTo) {
                dispatch(setValidationError({ field: "billingPeriodFrom", error: "error.billing-period-from-after-to" }));
                return;
            }
        }

        dispatch(setFormField({ field, value }));

        if (errors[field]) dispatch(setValidationError({ field, error: "" }));
        if (field === "billingPeriodFrom" && errors.billingPeriodTo) dispatch(setValidationError({ field: "billingPeriodTo", error: "" }));
        if (field === "billingPeriodTo" && errors.billingPeriodFrom) dispatch(setValidationError({ field: "billingPeriodFrom", error: "" }));
        if ((field === "billingPeriodFrom" || field === "billingPeriodTo") && errors.invoiceDate) {
            dispatch(setValidationError({ field: "invoiceDate", error: "" }));
        }
    };

    // Changing the arrival date recomputes the skonto deadline from it
    // (arrival + 1 + cashDiscountDays calendar days, Sundays rolled to Monday).
    // The user can still override the deadline afterwards by editing the
    // cash-discount-date field directly.
    const handleArrivalDateChange = (date) => {
        handleChange("invoiceArrivalDate", date);
        handleChange(
            "cashDiscountDate",
            computeCashDiscountDeadline(date, cashDiscountDays),
        );
    };

    const maxDate = formValues.invoiceDate || undefined;
    const minDateTo = formValues.billingPeriodFrom || undefined;
    const maxDateFrom = formValues.billingPeriodTo || undefined;

    return (
        <div className="invoice-fields-compact">

            {/* Invoice Number + Invoice Date */}
            <div className="field-pair">
                <TextFieldAtom
                    type="text"
                    label={t("invoice.invoice-number")}
                    placeholder="#"
                    required
                    name="invoiceNumber"
                    value={formValues.invoiceNumber || ""}
                    error={errors.invoiceNumber ? t(errors.invoiceNumber) : ""}
                    onChange={(e) => handleChange("invoiceNumber", e.target.value)}
                    disabled={readonly}
                />
                <DatePickerAtom
                    label={t("invoice.invoice-date")}
                    placeholderText="-"
                    selected={formValues.invoiceDate}
                    onChange={(date) => handleChange("invoiceDate", date)}
                    error={errors.invoiceDate ? t(errors.invoiceDate) : ""}
                    disabled={readonly}
                    required
                />
            </div>

            {/* Billing Period */}
            <div className="compact-section-label">{t("invoice.billing-period")}</div>
            <div className="field-pair">
                <DatePickerAtom
                    label={t("invoice.billing-period-from")}
                    placeholderText="-"
                    selected={formValues.billingPeriodFrom}
                    onChange={(date) => handleChange("billingPeriodFrom", date)}
                    disabled={readonly}
                    error={errors.billingPeriodFrom ? t(errors.billingPeriodFrom) : ""}
                    required
                    maxDate={
                        maxDate && maxDateFrom
                            ? maxDate < maxDateFrom ? maxDate : maxDateFrom
                            : maxDate || maxDateFrom
                    }
                />
                <DatePickerAtom
                    label={t("invoice.billing-period-to")}
                    placeholderText="-"
                    selected={formValues.billingPeriodTo}
                    onChange={(date) => handleChange("billingPeriodTo", date)}
                    disabled={readonly}
                    error={errors.billingPeriodTo ? t(errors.billingPeriodTo) : ""}
                    required
                    maxDate={maxDate}
                    minDate={minDateTo}
                />
            </div>

            {/* Order Number + Order Date */}
            <div className="field-pair">
                <TextFieldAtom
                    type="text"
                    label={t("invoice.order-number")}
                    placeholder="-"
                    name="orderNumber"
                    value={formValues.orderNumber || ""}
                    error={errors.orderNumber ? t(errors.orderNumber) : ""}
                    onChange={(e) => handleChange("orderNumber", e.target.value)}
                    disabled={readonly}
                />
                <DatePickerAtom
                    label={t("invoice.order-date")}
                    placeholderText="-"
                    selected={formValues.orderDate}
                    onChange={(date) => handleChange("orderDate", date)}
                    error={errors.orderDate ? t(errors.orderDate) : ""}
                    disabled={readonly}
                />
            </div>

            {/* Invoice Arrival Date + Cash Discount Date - only when the category
                has a cash discount (skonto) configured */}
            {hasCashDiscount && (
                <div className="field-pair">
                    <DatePickerAtom
                        label={t("invoice.invoice-arrival-date")}
                        placeholderText="-"
                        selected={formValues.invoiceArrivalDate}
                        onChange={handleArrivalDateChange}
                        error={errors.invoiceArrivalDate ? t(errors.invoiceArrivalDate) : ""}
                        disabled={readonly}
                        required
                    />
                    <DatePickerAtom
                        label={t("invoice.cash-discount-date")}
                        placeholderText="-"
                        selected={formValues.cashDiscountDate}
                        onChange={(date) =>
                            handleChange("cashDiscountDate", date)
                        }
                        error={errors.cashDiscountDate ? t(errors.cashDiscountDate) : ""}
                        disabled={readonly}
                        required
                        hint={cashDiscountDays != null
                            ? t("invoice.cash-discount-date-hint", { days: cashDiscountDays })
                            : undefined
                        }
                    />
                </div>
            )}

            {/* Partial / Final toggle */}
            <div className="partial-final-switch">
                <span className="switch-label">{t("invoice.partly-payment")}</span>
                <Form.Check
                    className="d-flex"
                    type="switch"
                    checked={formValues.isFinalInvoice || false}
                    onChange={(e) => handleChange("isFinalInvoice", e.target.checked)}
                    disabled={readonly}
                />
                <span className="switch-label">{t("invoice.final-invoice")}</span>
            </div>
            <p className="compact-hint-text">{t("invoice.final-invoice-help-text")}</p>

            {/* Internal Reference */}
            <TextFieldAtom
                type="text"
                label={t("invoice.internal-reference-number")}
                placeholder="-"
                value={formValues.internalReferenceNumber || ""}
                disabled={true}
                hint={t("invoice.internal-reference-auto-generated")}
            />

            {/* Historical Baseline Badge */}
            {formValues.isHistoricalBaseline && formValues.id && (
                <div className="compact-badge-row">
                    <Badge bg="info" className="py-1 px-2 d-inline-flex align-items-center">
                        <FontAwesomeIcon icon={faHistory} className="me-1" />
                        {t("invoice.baseline-badge")}
                        {formValues.historicalInvoices?.length > 0 && (
                            <Badge bg="light" text="dark" className="ms-1">
                                +{formValues.historicalInvoices.length}
                            </Badge>
                        )}
                    </Badge>
                </div>
            )}
        </div>
    );
}

export default InvoiceDetailFieldsV2;
